import { PasswordInput, TextInput } from "@/components/Input";
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FormControl,
  Link,
  Stack,
  Text
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { VendorLogin } from "../types";
import * as Yup from "yup";
import { useVendorLogin } from "../operations";
import { useRecoilState } from "recoil";
import { VendorState, VendorTokenState } from "../resources";
import { Navigate, useNavigate, NavLink } from "react-router-dom";
import { isExpired } from "@/utilities/get-user-token";
import handleErrorMessage from "@/utilities/get-error";

export default function Login() {
  const { loginVendor, loginVendorResult } = useVendorLogin();
  const [token, setVendorToken] = useRecoilState(VendorTokenState);
  const [, setVendorState] = useRecoilState(VendorState);
  const navigate = useNavigate();
  const { handleChange, values, errors, submitForm } = useFormik<VendorLogin>({
    initialValues: {
      identifier: "",
      password: ""
    },
    onSubmit: async () => {
      try {
        const { identifier, password } = values;
        const res = await loginVendor({ identifier, password });
        setVendorToken(res.token);
        setVendorState(res.data);
        navigate("/dashboard");
      } catch (err) {
        handleErrorMessage(err);
      }
    },
    validationSchema: Yup.object().shape({
      identifier: Yup.string().email().required(),
      password: Yup.string().required()
    })
  });
  if (!isExpired() && token) {
    // this should navigate to where the vendor is coming from
    return <Navigate to={"/dashboard"} />;
  } else {
    return (
      <Box>
        <Text
          fontWeight={"700"}
          fontSize={{ base: "16px", md: "18px" }}
          color={"var(--keza-brown)"}
        >
          Welcome back!
        </Text>
        <Text fontSize={{ base: "12px", md: "14px" }}>
          Enter your details correctly to sign in to your dashboard
        </Text>

        <Divider borderColor={"black"} opacity="0.4" my={"2rem"} />

        <FormControl>
          <Box maxW={{ base: "100%", md: "80%", lg: "70%" }}>
            <TextInput
              name="identifier"
              onChange={handleChange}
              label={"Email address"}
              isInvalid={Boolean(errors.identifier)}
              value={values.identifier}
            />
          </Box>

          <Box
            mt={{ base: "1.5rem", md: "2rem" }}
            maxW={{ base: "100%", md: "80%", lg: "70%" }}
          >
            <PasswordInput
              name="password"
              onChange={handleChange}
              label={"Password"}
              isInvalid={Boolean(errors.password)}
              value={values.password}
            />
            <Text
              mt={"01rem"}
              cursor="pointer"
              textAlign={"right"}
              fontSize={"14px"}
              textDecoration="underline"
              color={"var(--keza-brown)"}
            >
              <NavLink className={"small-brwn-form"}
                color={"var(--keza-brown)"} to="/auth/forgot-password">
                Forgot password?
              </NavLink>
            </Text>
          </Box>
        </FormControl>
        <Center
          as={Stack}
          mt={"4rem"}
          maxW={{ base: "100%", md: "80%", lg: "60%" }}
        >
          <Box>
            <Button
              color="white"
              bg="var(--keza-brown)"
              size={"lg"}
              _hover={{
                bg: "rgba(29, 29, 29, 0.1);"
              }}
              w="15rem"
              mb={"1rem"}
              onClick={submitForm}
              isLoading={loginVendorResult.isLoading}
            >
              Sign in
            </Button>
          </Box>
          <Flex gap="1rem" fontSize={"14px"} mb="3rem">
            <Text opacity={"0.7"}>I don’t have an account</Text>
            <Link
              href="/auth/sign-up"
              cursor={"pointer"}
              fontWeight={"600"}
              color="var(--keza-brown)"
            >
              Create an account
            </Link>
          </Flex>
        </Center>
      </Box>
    );
  }
}

export type LoginReqDto = {
  email: string;
  password: string;
};
